/**
 * Reading an `unknown` response body without pretending it is typed.
 *
 * The generated client returns `body: unknown`, on purpose: the spec's schemas are data and
 * the client does not assert them. These readers turn a wire value into a typed one or
 * raise — they never substitute a default, because a missing field on a documented response
 * is a server defect, and a console that renders `0` for it reports the defect as a fact.
 *
 * Moved from the portal's `apps/web-app/src/api/decode.ts` (plan-034 VIEW-PKG), which now
 * re-exports this module, so the portal and the wetalk app read a response the same way.
 */
export class MalformedResponseError extends Error {
    name = "MalformedResponseError";
    constructor(path, wanted, got) {
        super(`Response field "${path}" should be ${wanted}; received ${describe(got)}.`);
    }
}
function describe(value) {
    if (value === null)
        return "null";
    if (Array.isArray(value))
        return "an array";
    return typeof value;
}
export function readRecord(value, path) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new MalformedResponseError(path, "an object", value);
    }
    return value;
}
export function readArray(value, path) {
    if (!Array.isArray(value))
        throw new MalformedResponseError(path, "an array", value);
    return value;
}
export function readText(value, path) {
    if (typeof value !== "string")
        throw new MalformedResponseError(path, "a string", value);
    return value;
}
/** A string, or `null` when the wire says `null`; absent (`undefined`) is still malformed. */
export function readNullableText(value, path) {
    if (value === null)
        return null;
    return readText(value, path);
}
export function readWholeNumber(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new MalformedResponseError(path, "a number", value);
    }
    return value;
}
/** A number, or `null` when the wire says `null`; absent (`undefined`) is still malformed. */
export function readNullableWholeNumber(value, path) {
    if (value === null)
        return null;
    return readWholeNumber(value, path);
}
export function readBoolean(value, path) {
    if (typeof value !== "boolean")
        throw new MalformedResponseError(path, "a boolean", value);
    return value;
}
export function readTextArray(value, path) {
    return readArray(value, path).map((one, index) => readText(one, `${path}[${String(index)}]`));
}
/** `{ data: [...] }` — the envelope every bounded catalogue response uses. */
export function readDataArray(body, operationId) {
    return readArray(readRecord(body, operationId)["data"], `${operationId}.data`);
}
//# sourceMappingURL=decode.js.map